import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRecentActions } from '../contexts/RecentActionsContext';
import { useToast } from '../providers/ToastProvider';
import { submitRefinementRecords } from '../services/patient/refinement';

/**
 * Upload-refinement-records flow. The "Review for refinement" decision now lives
 * on the PatientReviewRefinement page (routed surface), so this hook only owns
 * the records-upload modal that follows approval.
 */
export function useRefinementWorkflow() {
    const queryClient = useQueryClient();
    const { showToast } = useToast();
    const { markActionCompleted } = useRecentActions();

    const [isUploadRefinementModalOpen, setIsUploadRefinementModalOpen] = useState(false);
    const [uploadRefinementPatientId, setUploadRefinementPatientId] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const openUploadRefinement = useCallback((patientId: string) => {
        setUploadRefinementPatientId(patientId);
        setIsUploadRefinementModalOpen(true);
    }, []);

    const closeUploadRefinement = useCallback(() => {
        setIsUploadRefinementModalOpen(false);
        setUploadRefinementPatientId(null);
    }, []);

    const handleUploadComplete = useCallback(
        async (refinementGoal: string) => {
            if (!uploadRefinementPatientId) return;

            setIsSubmitting(true);
            try {
                await submitRefinementRecords(uploadRefinementPatientId, refinementGoal);
                queryClient.invalidateQueries({ queryKey: ['patients'] });
                queryClient.invalidateQueries({ queryKey: ['patient', uploadRefinementPatientId] });
                markActionCompleted(uploadRefinementPatientId, 'Upload refinement records');
            } catch (error) {
                showToast('Failed to submit refinement records.', 'error');
            } finally {
                setIsSubmitting(false);
                setIsUploadRefinementModalOpen(false);
                setUploadRefinementPatientId(null);
            }
        },
        [uploadRefinementPatientId, queryClient, showToast, markActionCompleted]
    );

    return {
        isUploadRefinementModalOpen,
        uploadRefinementPatientId,
        isSubmitting,
        openUploadRefinement,
        closeUploadRefinement,
        handleUploadComplete
    };
}
